import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchFiles } from '../contexts/SearchFilesContext';
import Content from '../components/Content';
import SearchInterface from '../components/SearchInterface';
import SearchSummary from '../components/SearchSummary';
import AdvancedSearchPopup from '../components/AdvancedSearchPopup';
import { legalExplanationFiles } from '../file-manifest';
import { useNavigate } from 'react-router-dom';

function LegalExplanation() {
  const { getSelectedFiles, updateSelectedFiles, explanationSearchState, setExplanationSearchState, setSearchCount } = useSearchFiles();
  const [allFileNames] = useState(legalExplanationFiles);
  // Default to context state 
  const [activeLawFiles, setActiveLawFiles] = useState(getSelectedFiles('explanation'));
  
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const navigate = useNavigate();

  // Map context state
  const searchQuery = explanationSearchState.query;
  const submittedQuery = explanationSearchState.submittedQuery;
  const fileResults = explanationSearchState.results;
  const searchAttempted = explanationSearchState.attempted;

  const setSearchQuery = (val) => setExplanationSearchState(prev => ({ ...prev, query: val }));
  const setSubmittedQuery = (val) => setExplanationSearchState(prev => ({ ...prev, submittedQuery: val }));
  const setFileResults = (val) => setExplanationSearchState(prev => ({ ...prev, results: val }));
  const setSearchAttempted = (val) => setExplanationSearchState(prev => ({ ...prev, attempted: val }));
  const setScrollPosition = (val) => setExplanationSearchState(prev => ({ ...prev, scrollPosition: val }));
  
  const scrollRef = useRef(0);

  // Restore scroll position when results are rendered
  useEffect(() => {
    if (explanationSearchState.scrollPosition > 0 && explanationSearchState.results.length > 0) {
      const timer = setTimeout(() => {
        window.scrollTo(0, explanationSearchState.scrollPosition);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [explanationSearchState.results]);

  // Track scroll using useLayoutEffect
  React.useLayoutEffect(() => {
    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };
    
    scrollRef.current = window.scrollY;
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
       if (scrollRef.current > 0) {
        setScrollPosition(scrollRef.current);
      }
    };
  }, []);

  const handleSearch = () => {
    setSearchAttempted(true);
    setSubmittedQuery(searchQuery);
    setSearchCount(prevCount => prevCount + 1);
  };

  const handleApplyAdvancedSearch = (selectedFiles) => {
    setActiveLawFiles(selectedFiles);
    updateSelectedFiles(selectedFiles, 'explanation');
    setShowAdvancedSearch(false);
  };

  const handleResultClick = useCallback((resultId) => {
    navigate(`/legal-explanation/${resultId}`, { state: { id: resultId } });
  }, [navigate]);

  useEffect(() => {
    const normalizeText = (text) => {
      // Remove zero-width spaces that might be inserted by iOS Khmer keyboards
      let normalized = text.replace(/\u200B/g, '');
      // Normalize by finding "មាត្រា" followed by one or more spaces and a digit (Arabic or Khmer),
      // and removing the spaces.
      // Arabic digits: 0-9
      // Khmer digits: \u17E0-\u17E9
      return normalized.toLowerCase().replace(/មាត្រា\s+([0-9\u17E0-\u17E9])/g, 'មាត្រា$1');
    };

    const performSearch = async () => {
      if (!submittedQuery) {
        setFileResults([]);
        return;
      }
      setIsLoading(true);
      
      // Split query by comma, normalize each term
      const searchTerms = submittedQuery.split(',')
        .map(term => normalizeText(term.trim()))
        .filter(term => term.length > 0);

      const allResults = [];
      let resultId = 0;
      for (const fileName of activeLawFiles) {
        try {
          const response = await fetch(`/lawExplain/${fileName}`);
          const arrayBuffer = await response.arrayBuffer();
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(arrayBuffer);
          const segments = text.split(/(?=<)/);
          segments.forEach(segment => {
            if (segment.trim()) {
              const normalizedSegment = normalizeText(segment);
              // Check if segment contains ANY of the search terms
              const matches = searchTerms.some(term => normalizedSegment.includes(term));
              if (matches) {
                allResults.push({
                  id: resultId++,
                  name: segment.trim(),
                  source: fileName,
                });
              }
            }
          });
        } catch (error) {
          console.error(`Failed to fetch or process ${fileName}:`, error);
        }
      }
      setFileResults(allResults);
      setIsLoading(false);
    };
    if (searchAttempted) {
      performSearch();
    }
  }, [submittedQuery, searchAttempted, activeLawFiles]);

  useEffect(() => {
    const results = fileResults;
    results.forEach((result) => {
      result.onClick = () => handleResultClick(result.id);
    });
  }, [fileResults, handleResultClick]);

  return (
    <div
      className="legal-explanation-search"
      style={{
        marginTop: '0px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <SearchInterface
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        isLoading={isLoading}
        showAdvancedSearchButton={true}
        onToggleAdvancedSearch={() => setShowAdvancedSearch(!showAdvancedSearch)}
      />
      <SearchSummary
        searchAttempted={searchAttempted}
        searchQuery={submittedQuery}
        fileResultsCount={fileResults ? fileResults.length : 0}
      />
      {showAdvancedSearch && (
        <AdvancedSearchPopup
          allFileNames={allFileNames}
          initiallyActiveFiles={activeLawFiles}
          onApply={handleApplyAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          context="explanation"
        />
      )}
      <Content
        fileResults={fileResults}
        searchAttempted={searchAttempted}
        isLoading={isLoading}
        searchQuery={submittedQuery}
      />
      
    </div>
  );
}

export default LegalExplanation;

